// ------------------------------
// src/pages/FrmAjusteInventario.jsx
// ------------------------------
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Table,
  Input,
  Select,
  App as AntApp,
  Space,
  Typography,
  Grid,
  Tag,
  Tooltip,
  InputNumber,
  Button,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";

/**
 * FrmAjusteInventario
 * - PUT inventario con SOLO el ajuste convertido a base (sin sumar).
 * - Ajuste NO negativo (≥ 0).
 * - Flujo PlanConsumo tras PUT ok:
 *    GET /PlanConsumo?idProducto=&estado=1  → toma primero
 *    PUT /DetallePlanConsumo/estado-por-plan/{idPlan}
 *    Calcula consumoDiario/diasConsumo (por peso o numDias)
 *    POST /DetallePlanConsumo
 */

const BASE_URL = process.env.REACT_APP_API_URL || "https://localhost:7118/api";
const PAGE_SIZE = 20;
const { useBreakpoint } = Grid;

// Helpers
const toArray = (maybe) => {
  if (Array.isArray(maybe)) return maybe;
  if (maybe && Array.isArray(maybe.$values)) return maybe.$values;
  return [];
};
const roundInt = (n) => Math.max(0, Math.round(Number(n || 0)));

export default function FrmAjusteInventario() {
  const { message } = AntApp.useApp();
  const screens = useBreakpoint();

  // UI
  const [loading, setLoading] = useState(false);

  // Búsqueda / paginación
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Datos
  const [rows, setRows] = useState([]); // [{ productoId, idInventario, nombre, cantidad(base), tipoMedidaId, idUnidadMedida }]
  const [unitsByTipo, setUnitsByTipo] = useState({}); // { [tipoMedidaId]: Unidad[] }
  const unitsById = useMemo(() => {
    const m = new Map();
    Object.values(unitsByTipo).forEach((arr) => {
      (arr || []).forEach((u) => m.set(Number(u.id), u));
    });
    return m;
  }, [unitsByTipo]);

  // Unidad elegida por producto
  const [selectedUnitByProduct, setSelectedUnitByProduct] = useState({}); // { [productoId]: unidadId }

  // Ajuste y loading por fila
  const [ajusteByProduct, setAjusteByProduct] = useState({}); // { [productoId]: valorInput }
  const [rowLoading, setRowLoading] = useState({}); // { [productoId]: bool }

  // Debounce búsqueda
  const debounceRef = useRef(null);
  const onSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setPage(1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchData(1, val);
    }, 300);
  };

  // Unidades por tipo (cache)
  const fetchUnitsByTipo = useCallback(
    async (tipoMedidaId) => {
      if (!tipoMedidaId) return [];
      if (unitsByTipo[tipoMedidaId]) return unitsByTipo[tipoMedidaId];
      try {
        const res = await fetch(`${BASE_URL}/UnidadMedida/tipo/${tipoMedidaId}/activos`);
        if (!res.ok) throw new Error("Error al cargar unidades por tipo");
        const data = await res.json();
        const list = toArray(data);
        setUnitsByTipo((prev) => ({ ...prev, [tipoMedidaId]: list }));
        return list;
      } catch (err) {
        console.error(err);
        message.error("No se pudieron cargar las unidades (tipo).");
        return [];
      }
    },
    [message, unitsByTipo]
  );

  // Cargar datos iniciales
  useEffect(() => {
    fetchData(page, search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // GET inventario (igual a tu FrmInventarioTable + idInventario)
  const fetchData = async (pageArg = 1, searchArg = "") => {
    try {
      setLoading(true);
      const url = new URL(`${BASE_URL}/Producto/inventario`);
      url.searchParams.set("search", searchArg || "");
      url.searchParams.set("page", String(pageArg));
      url.searchParams.set("pageSize", String(PAGE_SIZE));

      const res = await fetch(url.toString());
      if (!res.ok) throw new Error("Error al cargar datos");
      const data = await res.json();

      const rawItems = data.items ?? data.Items ?? data;
      const items = toArray(rawItems);
      const totalVal = data.total ?? data.Total ?? items.length;
      setTotal(totalVal);

      const mapped = items.map((x) => ({
        key: x.productoId ?? x.ProductoId,
        productoId: x.productoId ?? x.ProductoId,
        idInventario: x.idInventario ?? x.IdInventario ?? x.inventarioId ?? x.InventarioId ?? null,
        nombre: x.nombre ?? x.Nombre,
        cantidad: x.cantidad ?? x.Cantidad, // EN UNIDAD BASE (referencia visual)
        tipoMedidaId: x.tipoMedidaId ?? x.TipoMedidaId,
        idUnidadMedida: x.idUnidadMedida ?? x.IdUnidadMedida,
      }));
      setRows(mapped);

      // Preselección de unidad
      setSelectedUnitByProduct((prev) => {
        const next = { ...prev };
        for (const r of mapped) {
          if (r.idUnidadMedida && next[r.productoId] == null) {
            next[r.productoId] = Number(r.idUnidadMedida);
          }
        }
        return next;
      });

      // Pre-carga de catálogos
      const tipos = Array.from(new Set(mapped.map((r) => r.tipoMedidaId).filter(Boolean)));
      tipos.forEach((t) => void fetchUnitsByTipo(t));

      setPage(pageArg);
    } catch (err) {
      console.error(err);
      message.error("No se pudieron cargar los productos.");
    } finally {
      setLoading(false);
    }
  };

  // Cambio de página
  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    fetchData(pageNumber, search);
  };

  // Selección de unidad por producto
  const handleUnitChange = (productoId, unitId) => {
    setSelectedUnitByProduct((prev) => ({ ...prev, [productoId]: Number(unitId) }));
  };

  // Cantidad mostrada (convertida a la unidad seleccionada) — solo visual
  const renderCantidad = (value, record) => {
    const originalBase = Number(value ?? 0);
    const rawUnitId = selectedUnitByProduct[record.productoId] ?? record.idUnidadMedida;
    const unitId = rawUnitId != null ? Number(rawUnitId) : undefined;
    if (!unitId) return originalBase;
    const unit = unitsById.get(unitId);
    if (!unit || !unit.factorBase || unit.factorBase === 0) return originalBase;
    const converted = originalBase / Number(unit.factorBase);
    return Number.isFinite(converted) ? converted : originalBase;
  };

  // Aplicar ajuste: PUT con ajuste convertido (≥ 0) y luego flujo PlanConsumo
  const onApplyAjuste = useCallback(
    async (record) => {
      const { productoId, idInventario } = record;
      const valUser = ajusteByProduct[productoId];

      // 1) Validar input
      if (valUser === undefined || valUser === null || valUser === "") {
        message.warning("Ingresa un valor de ajuste.");
        return;
      }
      const valNum = Number(String(valUser).replace(/\s+/g, "").replace(",", "."));
      if (!Number.isFinite(valNum)) {
        message.error("El ajuste no es numérico.");
        return;
      }
      if (valNum < 0) {
        message.error("El ajuste no puede ser negativo.");
        return;
      }

      // 2) Unidad activa
      const rawUnitId = selectedUnitByProduct[productoId] ?? record.idUnidadMedida;
      const unitId = rawUnitId != null ? Number(rawUnitId) : undefined;
      if (!unitId) {
        message.error("Selecciona una unidad de medida para aplicar el ajuste.");
        return;
      }
      const unit = unitsById.get(unitId);
      if (!unit || !unit.factorBase) {
        message.error("No se encontró el factorBase de la unidad.");
        return;
      }

      // 3) Conversión a base (este es el valor final que se envía)
      const ajusteBase = valNum * Number(unit.factorBase);
      if (!Number.isFinite(ajusteBase)) {
        message.error("No se pudo convertir el ajuste a unidad base.");
        return;
      }

      setRowLoading((st) => ({ ...st, [productoId]: true }));
      try {
        // 4) PUT inventario con SOLO el ajuste convertido
        {
          const url = `${BASE_URL}/Inventario/producto/${productoId}/cantidad`;
          const res = await fetch(url, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ cantidad: ajusteBase }),
          });
          if (!res.ok) throw new Error("PUT inventario falló");
        }

        message.success("Ajuste aplicado");

        // (Opcional) reflejar visualmente lo enviado
        setRows((prev) =>
          prev.map((r) => (r.productoId === productoId ? { ...r, cantidad: ajusteBase } : r))
        );

        // 5) Flujo PlanConsumo SOLO si hay idInventario
        const ensuredIdInventario = idInventario;
        const ensuredCantidadInv = Number(ajusteBase);

        if (ensuredIdInventario) {
          // 5.1) GET PlanConsumo por producto (estado=1)
          let plan = null;
          try {
            const urlPlan = new URL(`${BASE_URL}/PlanConsumo`);
            urlPlan.searchParams.set("idProducto", String(productoId));
            urlPlan.searchParams.set("estado", "1");
            const planRes = await fetch(urlPlan.toString());
            if (planRes.ok) {
              const raw = await planRes.json();
              const arr = Array.isArray(raw)
                ? raw
                : Array.isArray(raw?.$values)
                ? raw.$values
                : Array.isArray(raw?.data)
                ? raw.data
                : [];
              plan = arr[0] || null;
            }
          } catch (err) {
            console.warn("Sin PlanConsumo para producto", productoId, err);
          }

          // Si no hay plan, no hacemos nada más
          if (plan && plan !== null) {
            const idPlan = plan.id ?? plan.Id ?? null;
            const peso = plan.peso ?? plan.Peso; // puede ser null
            const numDias = plan.numDias ?? plan.NumDias; // puede ser null

            // 5.2) Cerrar/actualizar estados previos
            try {
              if (idPlan != null) {
                await fetch(`${BASE_URL}/DetallePlanConsumo/estado-por-plan/${idPlan}`, {
                  method: "PUT",
                });
              }
            } catch (err) {
              console.warn("PUT /DetallePlanConsumo/estado-por-plan falló:", err);
            }

            // 5.3) Calcular consumoDiario / diasConsumo
            let consumoDiario = 0;
            let diasConsumo = 0;
            if (peso != null) {
              diasConsumo = roundInt(ensuredCantidadInv / Number(peso || 1));
              consumoDiario = roundInt(Number(peso));
            } else if (numDias != null && Number(numDias) > 0) {
              consumoDiario = roundInt(ensuredCantidadInv / Number(numDias || 1));
              diasConsumo = Number(numDias);
            }

            // 5.4) POST DetallePlanConsumo
            const dpcPayload = {
              idInventario: ensuredIdInventario,
              idPlanConsumo: idPlan,
              fecha: new Date().toISOString(),
              estado: 1,
              consumoDiario: Number(consumoDiario),
              diasConsumo: Number(diasConsumo),
              cantidadActual: ensuredCantidadInv,
            };
            try {
              await fetch(`${BASE_URL}/DetallePlanConsumo`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(dpcPayload),
              });
            } catch (err) {
              console.error("Error POST /DetallePlanConsumo:", err);
            }
          }
        }

        // Limpia input
        setAjusteByProduct((st) => ({ ...st, [productoId]: undefined }));
      } catch (err) {
        console.error(err);
        message.error("Error al aplicar el ajuste.");
      } finally {
        setRowLoading((st) => ({ ...st, [productoId]: false }));
      }
    },
    [ajusteByProduct, message, selectedUnitByProduct, unitsById]
  );

  // Columnas
  const columns = useMemo(
    () => [
      {
        title: "Producto",
        dataIndex: "nombre",
        key: "nombre",
        ellipsis: true,
        width: 220,
        render: (txt) => <Typography.Text strong>{txt}</Typography.Text>,
      },
      {
        title: "Cantidad",
        dataIndex: "cantidad",
        key: "cantidad",
        render: renderCantidad,
        align: "right",
        width: 130,
        responsive: ["sm"],
      },
      {
        title: "U Medida",
        key: "uMedida",
        width: 260,
        render: (_, record) => {
          const rawUnitId = selectedUnitByProduct[record.productoId] ?? record.idUnidadMedida;
          const unitId = rawUnitId != null ? Number(rawUnitId) : undefined;
          const options = (unitsByTipo[record.tipoMedidaId] || []).map((u) => ({
            value: Number(u.id),
            label: `${u.nombre} (${u.abreviatura})`,
          }));
          return (
            <Space size={6}>
              <Tag>{unitsById.get(unitId)?.abreviatura ?? "-"}</Tag>
              <Tooltip title={`factorBase: ${unitsById.get(unitId)?.factorBase ?? "?"}`}>
                <Select
                  value={unitId}
                  allowClear
                  placeholder="Seleccione unidad"
                  style={{ minWidth: 180, width: "100%" }}
                  onChange={(val) => handleUnitChange(record.productoId, val)}
                  options={options}
                  showSearch
                  optionFilterProp="label"
                />
              </Tooltip>
            </Space>
          );
        },
      },
      {
        title: "Ajuste",
        key: "ajuste",
        width: 280,
        render: (_, record) => {
          const productoId = record.productoId;
          const value = ajusteByProduct[productoId];
          const loadingBtn = !!rowLoading[productoId];

          const activeUnitId =
            selectedUnitByProduct[productoId] ?? record.idUnidadMedida ?? undefined;
          const activeUnit = activeUnitId ? unitsById.get(Number(activeUnitId)) : null;

          return (
            <Space.Compact style={{ width: "100%" }}>
              <InputNumber
                style={{ width: "70%" }}
                size="small"
                placeholder={`Ajuste en ${activeUnit?.abreviatura ?? "?"}`}
                value={value}
                onChange={(v) =>
                  setAjusteByProduct((st) => ({ ...st, [productoId]: v }))
                }
                step={0.01}
                stringMode
                min={0} // 🚫 no negativos
                max={Number.MAX_SAFE_INTEGER}
                formatter={(val) => (val === null || val === undefined ? "" : String(val))}
                parser={(val) => (val ? val.replace(/\s+/g, "").replace(",", ".") : "")}
                onPressEnter={() => onApplyAjuste(record)}
              />
              <Button
                size="small"
                icon={<CheckOutlined />}
                type="primary"
                loading={loadingBtn}
                disabled={value === undefined || value === null || value === ""}
                onClick={() => onApplyAjuste(record)}
              >
                Aplicar
              </Button>
            </Space.Compact>
          );
        },
      },
    ],
    [
      ajusteByProduct,
      onApplyAjuste,
      rowLoading,
      selectedUnitByProduct,
      unitsById,
      unitsByTipo,
    ]
  );

  return (
    <Space
      direction="vertical"
      style={{ width: "100%", padding: screens.xs ? 8 : 16 }}
      size={screens.xs ? "middle" : "large"}
    >
      <Typography.Title level={screens.xs ? 5 : 4} style={{ margin: 0 }}>
        Ajuste de Inventario
      </Typography.Title>

      <Input
        allowClear
        placeholder="Buscar producto o código de barras"
        onChange={onSearchChange}
        size={screens.xs ? "middle" : "large"}
      />

      <Table
        bordered
        size={screens.xs ? "small" : "middle"}
        loading={loading}
        columns={columns}
        dataSource={rows}
        rowKey={(r) => r.productoId}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          responsive: true,
          showSizeChanger: false,
          onChange: handlePageChange,
        }}
        scroll={{ x: 720 }}
        sticky
      />
    </Space>
  );
}
